import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import api, { getApiErrorMessage } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";

export default function RegulationGate() {
  const { user, logout } = useAuth();
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [checked, setChecked] = useState(false);
  const [read, setRead] = useState(false);
  const contentRef = useRef(null);
  const [session] = useState(() => crypto.randomUUID());
  const load = useCallback(async () => {
    setLoading(true); setError(""); setChecked(false);
    try { const { data } = await api.get("/regulamentos/pendentes", { headers: { "X-Regulation-Session": session } }); setPending(data); }
    catch (failure) { setError(getApiErrorMessage(failure)); }
    finally { setLoading(false); }
  }, [session]);
  useEffect(() => {
    api.defaults.headers.common["X-Regulation-Session"] = session;
    void load();
    const onFocus = () => { void load(); };
    window.addEventListener("focus", onFocus);
    return () => { window.removeEventListener("focus", onFocus); delete api.defaults.headers.common["X-Regulation-Session"]; };
  }, [load, session, user?.id]);
  const current = pending[0];
  useEffect(() => {
    setChecked(false); setRead(false);
    const node = contentRef.current;
    if (!node) return;
    node.scrollTop = 0;
    const measure = () => { if (node.scrollHeight <= node.clientHeight + 2) setRead(true); };
    const frame = requestAnimationFrame(measure);
    const observer = new ResizeObserver(measure); observer.observe(node);
    return () => { cancelAnimationFrame(frame); observer.disconnect(); };
  }, [current?.id]);
  const accept = async () => {
    setLoading(true);
    try { await api.post("/regulamentos/aceites", { versao_id: current.id, hash: current.hash }, { headers: { "X-Regulation-Session": session } }); await load(); }
    catch (failure) { setError(getApiErrorMessage(failure)); setLoading(false); }
  };
  return <Dialog open={loading || Boolean(error) || Boolean(current)}><DialogContent className="sm:max-w-2xl [&>button]:hidden" aria-describedby={undefined} onInteractOutside={(event) => event.preventDefault()} onEscapeKeyDown={(event) => event.preventDefault()}>
    <DialogHeader><DialogTitle>{current?.titulo || "Regulamentos"}</DialogTitle></DialogHeader>
    {current && <><p className="text-xs text-slate-500">{current.tipo === "geral" ? "Regulamento geral" : "Regulamento da campanha"} · Versão {current.versao}</p>
      <div ref={contentRef} tabIndex={0} aria-label="Texto do regulamento" className="max-h-[45vh] overflow-y-auto whitespace-pre-wrap break-words border-y py-4 text-sm leading-6" onScroll={(event) => { const node = event.currentTarget; if (node.scrollHeight - node.scrollTop <= node.clientHeight + 8) setRead(true); }}>{current.conteudo}</div>
      <label className="flex items-start gap-3 text-sm"><Checkbox checked={checked} disabled={!read || loading} onCheckedChange={(value) => setChecked(Boolean(value))} />Li e aceito esta versão do regulamento.</label></>}
    {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
    {loading && <p role="status" className="text-sm text-slate-500">Verificando regulamentos...</p>}
    <div className="flex flex-wrap justify-end gap-2"><Button variant="ghost" onClick={logout} disabled={loading}>Sair</Button>{error ? <Button onClick={load} disabled={loading}>Tentar novamente</Button> : current && <Button disabled={loading || !checked || !read} onClick={accept}>Aceitar e continuar</Button>}</div>
  </DialogContent></Dialog>;
}
